'use client';

import { useState, useRef, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { createLogger } from '@/shared/lib/logger';
import { useSectionChanges } from '@/features/design/render/model/render-form-store.selectors';
import { useRenderStore } from '@/features/design/render/model/render-form.store';
import { rendersService as renderService } from '@/shared/api/services/renders.service';
import { ApiClientError } from '@/shared/lib/api';
import { useLimitErrorPaywall } from '@/shared/hooks/useLimitErrorPaywall';
import { useTokenBalance } from '@/features/subscription/model/subscription.selectors';
import {
  trackEvent,
  AnalyticsEvent,
  BlockReason,
  ErrorType,
} from '@/shared/lib/analytics';
import { useAlbumSettingsStore } from '@/features/design/album/model/album-settings.store';
import { clearDraftAllModes } from '@/features/design/render/lib/render-settings-storage';
import { migrateLegacyProductPicks } from '@/features/design/render/lib/brand-tokens';
import { toast } from '@/shared/lib/toast';
import {
  ConceptAngleType,
  type EnvironmentSetting,
  EnvironmentSettingEnum,
  generateSeed,
  RenderStatus,
} from '@/shared/constants';
import type { ConceptAngle, Control, Angle, Concept } from '@/types/concept';
import type {
  Render,
  RenderCreateRequest,
  RenderModelFamily,
  RenderEffort,
  RenderSettings,
} from '@/types/render';
import type { Project } from '@/types/project';
import type { ScrollableRenderSettingsRef } from '../ui/scrollable-settings';

const logger = createLogger('useRenderPanelLogic');

export interface UseRenderPanelLogicProps {
  project: Project;
  concept: Concept;
  conceptAngles: ConceptAngle[];
  angles: Record<string, Angle>;
  renders: Record<string, Render[]>;
  controls: Record<string, Control[]>;
  angleId?: string;
  /** Currently selected render ID in the derivative angle carousel.
   * When user switches renders, settings panel must show the selected render's settings,
   * not always the latest render. */
  selectedRenderId?: string;
  onRenderCreated?: (render: Render) => void;
  onRefetch?: () => void;
  onClose?: () => void;
  enabled?: boolean;
}

export interface UseRenderPanelLogicReturn {
  // Computed data
  mainAngle: ConceptAngle | undefined;
  currentAngleControls: Control[];
  angle: Angle | null;
  defaultValues: Partial<RenderCreateRequest['data']['attributes']>;
  angleTabs: Array<{ id: string; name: string; isMain: boolean }>;
  mainRenderSettings: RenderSettings | null | undefined;
  /** A refine render is in flight for the current angle — the panel becomes a
   *  read-only readout of what's rendering (its launched settings + Refine
   *  mode), with the parent render shown as the angle image. Explore-in-progress
   *  is unaffected (its "+1" path stays editable). Mirrors Apple/Android. */
  isRefineInProgress: boolean;
  /** Parent render image (the "before" being refined) + id of the in-flight
   *  refine render — point the toggle at it so the Refine pill shows and the
   *  card shows the image under iteration. Null when not refining. */
  refineParentRenderUrl: string | null;
  refineParentRenderId: string | null;

  // State
  isCreating: boolean;
  error: string | null;
  hasAnyChanges: boolean;

  // Actions
  handleCreateRender: (data: RenderCreateRequest) => Promise<void>;
  resetChangesToDefaults: () => void;
  renderSettingsRef: React.RefObject<ScrollableRenderSettingsRef | null>;

  // Translations
  tButton: ReturnType<typeof useTranslations>;
}

export function useRenderPanelLogic({
  project,
  concept,
  conceptAngles,
  angles,
  renders,
  controls,
  angleId,
  selectedRenderId,
  onRenderCreated,
  onClose,
  enabled: _enabled = true,
}: UseRenderPanelLogicProps): UseRenderPanelLogicReturn {
  const router = useRouter();
  const tButton = useTranslations('render.button');
  const tCommon = useTranslations('common');

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Route server-driven paywall errors (INSUFFICIENT_TOKENS, render limits, …)
  // through the shared dialog. When a paywall is shown we skip the local
  // toast/inline-error fallback so the same error doesn't surface twice.
  const routeToPaywall = useLimitErrorPaywall();
  const tokensBalance = useTokenBalance();

  // Ref to ScrollableRenderSettings component for imperative submit
  const renderSettingsRef = useRef<ScrollableRenderSettingsRef | null>(null);

  // Get form changes, reset function, and setDefaultValues
  const sectionChanges = useSectionChanges();
  const resetChangesToDefaults = useRenderStore(
    (state) => state.resetChangesToDefaults
  );
  const setDefaultValues = useRenderStore((state) => state.setDefaultValues);

  // Check if any section has changes
  const hasAnyChanges = useMemo(() => {
    return Object.values(sectionChanges).some((hasChange) => hasChange);
  }, [sectionChanges]);

  // Extract needed data from props
  const {
    mainAngle,
    currentAngle,
    currentAngleControls,
    mainRenderSettings,
    derivativeRenderSettings,
    isRefineInProgress,
    refineParentRenderUrl,
    refineParentRenderId,
  } = useMemo(() => {
    const mainAngle = conceptAngles.find(
      (a) => a.attributes.type === ConceptAngleType.MAIN
    );

    let currentAngle: ConceptAngle | null = null;
    let currentAngleControls: Control[] = [];
    let mainRenderSettings: RenderSettings | null | undefined = null;
    let derivativeRenderSettings: RenderSettings | null | undefined = null;

    // Get main render settings from render.attributes.settings
    if (mainAngle) {
      const angleRenders = renders[mainAngle.id] || [];
      const settingsRender = angleRenders
        .filter(
          (r) =>
            r.attributes.render_type === 'final_photorealistic' ||
            r.attributes.render_type === 'intermediate_photorealistic'
        )
        .sort((a, b) => {
          return (
            new Date(b.attributes.created_at).getTime() -
            new Date(a.attributes.created_at).getTime()
          );
        })[0];

      mainRenderSettings = settingsRender?.attributes.settings as
        | RenderSettings
        | null
        | undefined;
    }

    if (angleId) {
      // Derivative angle mode
      currentAngle = conceptAngles.find((a) => a.id === angleId) || null;
      if (currentAngle?.relationships?.angle?.data) {
        const angleDataId = currentAngle.relationships.angle.data.id;
        currentAngleControls = controls[angleDataId] || [];

        // Get derivative render settings from the currently selected render.
        // Derivative angles can have multiple renders with different settings.
        // When user switches renders in the carousel, the settings panel must
        // show the settings of the SELECTED render, not always the latest one.
        // API inherits source render settings for styled renders, so type filtering
        // is not needed — all carousel renders have settings.
        const angleRenders = renders[currentAngle.id] || [];

        // 1. Try selected render first (user explicitly chose this render in carousel)
        let settingsRender = selectedRenderId
          ? angleRenders.find((r) => r.id === selectedRenderId)
          : undefined;

        // 2. Fall back to latest render with settings
        if (!settingsRender || !settingsRender.attributes.settings) {
          settingsRender = angleRenders
            .filter((r) => r.attributes.settings)
            .sort(
              (a, b) =>
                new Date(b.attributes.created_at).getTime() -
                new Date(a.attributes.created_at).getTime()
            )[0];
        }

        derivativeRenderSettings = settingsRender?.attributes.settings as
          | RenderSettings
          | null
          | undefined;
      }
    } else {
      // Main angle mode
      currentAngle = mainAngle || null;
      if (mainAngle?.relationships?.angle?.data) {
        const angleDataId = mainAngle.relationships.angle.data.id;
        currentAngleControls = controls[angleDataId] || [];
      }
    }

    // In-flight refine render for the CURRENT angle (status pending/processing,
    // mode refine). While present the panel is a READ-ONLY readout of what's
    // rendering: seed the displayed settings from the in-flight render's OWN
    // launched snapshot (not the latest completed render or blank defaults), and
    // surface its parent render so the card/toggle show the image being refined.
    // A refine creates a fresh concept with no completed render, so without this
    // the panel would fall through to a blank Explore form and lose all trace of
    // what the user just launched. Explore-in-progress is intentionally
    // untouched — its parallel "+1" path stays live and editable.
    const inflightRefineRender = currentAngle
      ? (renders[currentAngle.id] || []).find(
          (r) =>
            r.attributes.mode === 'refine' &&
            (r.attributes.status === RenderStatus.PENDING ||
              r.attributes.status === RenderStatus.PROCESSING)
        )
      : undefined;

    if (inflightRefineRender?.attributes.settings) {
      const inflightSettings = inflightRefineRender.attributes
        .settings as RenderSettings;
      // Feed the existing defaultValues mapping below from the in-flight
      // settings so the read-only readout shows exactly what was launched.
      if (angleId) {
        derivativeRenderSettings = inflightSettings;
      } else {
        mainRenderSettings = inflightSettings;
      }
    }

    return {
      mainAngle,
      currentAngle,
      currentAngleControls,
      mainRenderSettings,
      derivativeRenderSettings,
      isRefineInProgress: !!inflightRefineRender,
      refineParentRenderUrl:
        inflightRefineRender?.relationships?.parent_render?.meta?.file_url ??
        null,
      refineParentRenderId:
        inflightRefineRender?.relationships?.parent_render?.data?.id ?? null,
    };
  }, [conceptAngles, angleId, selectedRenderId, controls, renders]);

  // Get the actual angle data
  const angle = useMemo(() => {
    if (!currentAngle?.relationships?.angle?.data) return null;
    return angles[currentAngle.relationships.angle.data.id] || null;
  }, [currentAngle, angles]);

  // Build angle tabs list. Apple parity: main angle is labelled
  // "Main angle"; derivative tabs use sequential "#N" indexing
  // (n = 2,3,…) regardless of custom angle name. Custom names live in
  // the angle card overlay/menu, not in the compact tab strip.
  const angleTabs = useMemo(() => {
    const sorted = [...conceptAngles].sort((a, b) => {
      const aMain = a.attributes.type === ConceptAngleType.MAIN;
      const bMain = b.attributes.type === ConceptAngleType.MAIN;
      if (aMain && !bMain) return -1;
      if (!aMain && bMain) return 1;
      return a.attributes.order - b.attributes.order;
    });
    let derivativeIndex = 1;
    return sorted.map((ca) => {
      const isMain = ca.attributes.type === ConceptAngleType.MAIN;
      if (isMain) {
        return { id: ca.id, name: 'Main angle', isMain: true };
      }
      derivativeIndex += 1;
      return { id: ca.id, name: `#${derivativeIndex}`, isMain: false };
    });
  }, [conceptAngles]);

  // Handle render creation
  const handleCreateRender = useCallback(
    async (data: RenderCreateRequest) => {
      const targetAngle = angleId ? { id: angleId } : mainAngle;

      if (!targetAngle) {
        setError('No angle found');
        return;
      }

      try {
        setIsCreating(true);
        setError(null);

        const render = await renderService.createRender(
          concept.id,
          data,
          targetAngle.id
        );

        const submittedSettings = data.data.attributes;

        trackEvent(AnalyticsEvent.RENDER_CREATED, {
          mode: submittedSettings.mode ?? 'explore',
          angle_kind: angleId ? 'derivative' : 'main',
          tokens_balance: tokensBalance,
        });

        if (!angleId) {
          // Main angle render
          const newConceptId =
            render.relationships?.concept_angle?.meta?.concept_id;

          if (newConceptId && newConceptId !== concept.id) {
            // Copy album settings (including requirements) to new concept
            useAlbumSettingsStore
              .getState()
              .copyConceptSettings(concept.id, newConceptId);
            // Wipe the leaving concept's main-angle drafts (both modes +
            // active-mode pointer). Apple parity: RenderManager clears
            // post-success; here the user is also leaving this conceptId
            // entirely, so neither Explore nor Refine intent applies to
            // the now-archived concept anymore.
            clearDraftAllModes(concept.id, undefined, undefined);
            // `?created=1`: one-shot flag so the destination concept header
            // plays the "new design created" pulse. ConceptEditor strips it
            // right after firing (refresh/back never replay it).
            router.push(
              `/project/${project.id}/concept/${newConceptId}?created=1`
            );
          } else {
            // Pass `submittedSettings.mode` so the store knows the new source
            // render's mode — drives `getExploreBaseline()` (RENDER FORM SPEC
            // rule 1) for diffs, resets, and section-change markers.
            setDefaultValues(submittedSettings, submittedSettings.mode);
            // RENDER FORM SPEC rules 3-4: Explore submit clears the Explore
            // draft and snaps the form to the new defaults; Refine submit
            // leaves both the form and draft untouched (refine sacred —
            // the user's prompt is iterative).
            if (submittedSettings.mode === 'explore') {
              resetChangesToDefaults();
            }
            onClose?.();
            onRenderCreated?.(render);
          }
        } else {
          // Derivative angle render
          setDefaultValues(submittedSettings, submittedSettings.mode);
          // RENDER FORM SPEC rules 3-4: see note above.
          if (submittedSettings.mode === 'explore') {
            resetChangesToDefaults();
          }
          onClose?.();
          onRenderCreated?.(render);
        }
      } catch (err) {
        setIsCreating(false);

        // Funnel analytics: typed limits that raced past the preflight count as
        // a block (same vocabulary as the preflight gate); everything else is a
        // genuine render_error.
        const mode = data.data.attributes.mode ?? 'explore';
        if (err instanceof ApiClientError) {
          if (err.isInsufficientTokensError()) {
            trackEvent(AnalyticsEvent.GENERATE_BLOCKED, {
              reason: BlockReason.INSUFFICIENT_TOKENS,
              mode,
              tokens_balance: tokensBalance,
            });
          } else if (err.isQueueLimitError() || err.isRenderQueueLimitError()) {
            // Legacy 403 detail OR the typed 422 RENDER_QUEUE_LIMIT_EXCEEDED —
            // both are the same queue saturation in the funnel vocabulary.
            trackEvent(AnalyticsEvent.GENERATE_BLOCKED, {
              reason: BlockReason.QUEUE_FULL,
              mode,
              tokens_balance: tokensBalance,
            });
          } else {
            trackEvent(AnalyticsEvent.RENDER_ERROR, {
              error_type: err.isValidationError()
                ? ErrorType.VALIDATION
                : err.isServerError()
                  ? ErrorType.SERVER
                  : ErrorType.UNKNOWN,
              mode,
            });
          }
        } else {
          trackEvent(AnalyticsEvent.RENDER_ERROR, {
            error_type: ErrorType.UNKNOWN,
            mode,
          });
        }

        // API-driven copy: title is a localized generic, description comes
        // from the server; surface non-typed errors via inline `setError`.
        if (err instanceof ApiClientError) {
          // ALL typed limit errors (insufficient tokens, queue/daily/monthly
          // caps, feature gates) → server-driven paywall. Android parity:
          // every typed limit routes to the paywall presenter. If the route
          // succeeds we suppress the local toast — otherwise (untyped error,
          // or older server with no paywall body) fall back below.
          if (routeToPaywall(err)) {
            return;
          }
          const isWarning =
            err.isDailyLimitError() || err.isQueueLimitError();
          if (isWarning || err.isInsufficientTokensError()) {
            const description =
              err.getFirstError() || tCommon('errors.generic');
            const fn = isWarning ? toast.warning : toast.error;
            fn(isWarning ? tCommon('warning') : tCommon('error'), {
              description,
            });
          } else {
            setError(err.getFirstError() || tCommon('errors.generic'));
          }
        } else {
          setError(tCommon('errors.generic'));
          logger.error('Unexpected render creation error:', err);
        }
      } finally {
        setIsCreating(false);
      }
    },
    [
      angleId,
      mainAngle,
      concept.id,
      project.id,
      router,
      setDefaultValues,
      resetChangesToDefaults,
      onClose,
      onRenderCreated,
      tCommon,
      routeToPaywall,
    ]
  );

  // Memoize defaultValues
  const defaultValues = useMemo(() => {
    // For derivative angles
    if (angleId) {
      const inheritedOutputStyleId = mainRenderSettings?.output_style_id
        ? typeof mainRenderSettings.output_style_id === 'string'
          ? parseInt(String(mainRenderSettings.output_style_id), 10)
          : (mainRenderSettings.output_style_id as number)
        : null;

      const inheritedPromptConcept =
        mainRenderSettings?.prompts?.prompt_concept || '';
      const inheritedEnvironmentSetting =
        (mainRenderSettings?.environment_setting as EnvironmentSetting) ||
        EnvironmentSettingEnum.NO_CHANGE;
      const inheritedRenderModelFamily =
        (mainRenderSettings?.render_model_family as RenderModelFamily) ||
        'AURORA';
      const inheritedRenderEffort =
        (mainRenderSettings?.render_effort as RenderEffort) || 'BRAINSTORM';
      // Pro+ engine toggles — absent in old snapshots (the API only recently
      // started serializing these keys) → off.
      const inheritedIsHires = (mainRenderSettings?.is_hires as boolean) ?? false;
      const inheritedIsExtendedThinking =
        (mainRenderSettings?.is_extended_thinking as boolean) ?? false;

      if (derivativeRenderSettings) {
        const derivativeOutputStyleIdRaw =
          derivativeRenderSettings.output_style_id;
        const derivativeOutputStyleId =
          typeof derivativeOutputStyleIdRaw === 'string'
            ? parseInt(String(derivativeOutputStyleIdRaw), 10)
            : (derivativeOutputStyleIdRaw as number | null | undefined);

        return {
          version: (derivativeRenderSettings.version as number) || 1,
          seed: derivativeRenderSettings.seed as number,
          prompts: {
            prompt_concept: inheritedPromptConcept,
            prompt_angle: derivativeRenderSettings.prompts?.prompt_angle || '',
          },
          controls: Array.isArray(derivativeRenderSettings.controls)
            ? derivativeRenderSettings.controls
            : null,
          style_ids: (derivativeRenderSettings.style_ids as number[]) ?? null,
          material_ids:
            (derivativeRenderSettings.material_ids as number[]) ?? null,
          // A render made before Brands existed kept its product picks inside
          // `colors`; loading it into the form lifts them onto the Brands axis,
          // where the picker that owns them now lives.
          ...migrateLegacyProductPicks(
            derivativeRenderSettings.colors as string[] | null,
            derivativeRenderSettings.brand_selections as string[] | null
          ),
          // Prefer derivative's own value when set; fall back to inherited from main.
          // This matches Apple/Android behavior now that the derivative panel
          // exposes Output style / Environment / Model selectors.
          output_style_id:
            derivativeOutputStyleId ?? inheritedOutputStyleId,
          environment_setting:
            (derivativeRenderSettings.environment_setting as EnvironmentSetting) ||
            inheritedEnvironmentSetting,
          render_model_family:
            (derivativeRenderSettings.render_model_family as RenderModelFamily) ||
            inheritedRenderModelFamily,
          render_effort:
            (derivativeRenderSettings.render_effort as RenderEffort) ||
            inheritedRenderEffort,
          is_hires:
            (derivativeRenderSettings.is_hires as boolean) ?? inheritedIsHires,
          is_extended_thinking:
            (derivativeRenderSettings.is_extended_thinking as boolean) ??
            inheritedIsExtendedThinking,
        };
      }

      // No prior derivative render → seed this angle's default sketch (if any).
      // Each derivative angle uses its OWN angle's default_control_id. When the
      // angle has no usable sketch the server sends null → empty selection.
      const derivativeDefaultControlId =
        currentAngle?.attributes.default_control_id;

      return {
        version: 1,
        seed: generateSeed(),
        prompts: {
          prompt_concept: inheritedPromptConcept,
          prompt_angle: '',
        },
        controls: derivativeDefaultControlId
          ? [{ id: derivativeDefaultControlId }]
          : null,
        style_ids: null,
        material_ids: null,
        colors: null,
        brand_selections: null,
        output_style_id: inheritedOutputStyleId,
        environment_setting: inheritedEnvironmentSetting,
        render_model_family: inheritedRenderModelFamily as RenderModelFamily,
        render_effort: inheritedRenderEffort as RenderEffort,
        is_hires: inheritedIsHires,
        is_extended_thinking: inheritedIsExtendedThinking,
      };
    }

    // For main angle
    if (mainRenderSettings) {
      return {
        version: (mainRenderSettings.version as number) || 1,
        seed: mainRenderSettings.seed as number,
        style_ids: mainRenderSettings.style_ids as number[],
        material_ids: mainRenderSettings.material_ids as number[],
        // See the derivative branch: pre-Brands picks move onto their own axis.
        ...migrateLegacyProductPicks(
          mainRenderSettings.colors as string[] | null,
          mainRenderSettings.brand_selections as string[] | null
        ),
        prompts: mainRenderSettings.prompts || {
          prompt_concept: '',
          prompt_angle: '',
        },
        controls: Array.isArray(mainRenderSettings.controls)
          ? mainRenderSettings.controls
          : null,
        output_style_id:
          typeof mainRenderSettings.output_style_id === 'string'
            ? parseInt(String(mainRenderSettings.output_style_id), 10)
            : (mainRenderSettings.output_style_id as number) || null,
        environment_setting:
          (mainRenderSettings.environment_setting as EnvironmentSetting) ||
          EnvironmentSettingEnum.NO_CHANGE,
        render_model_family:
          mainRenderSettings.render_model_family || 'AURORA',
        render_effort: mainRenderSettings.render_effort || 'BRAINSTORM',
        // Absent in snapshots taken before the setting existed → the no-op
        // middle, which is exactly how those renders were generated.
        render_ambition: mainRenderSettings.render_ambition || 'BALANCED',
        // Project references
        style_project_reference_ids:
          (mainRenderSettings.style_project_reference_ids as string[]) ?? null,
        material_project_reference_ids:
          (mainRenderSettings.material_project_reference_ids as string[]) ??
          null,
        color_project_reference_ids:
          (mainRenderSettings.color_project_reference_ids as string[]) ?? null,
        output_project_reference_ids:
          (mainRenderSettings.output_project_reference_ids as string[]) ?? null,
        use_auto_style_refs:
          (mainRenderSettings.use_auto_style_refs as boolean) ?? true,
        use_auto_material_refs:
          (mainRenderSettings.use_auto_material_refs as boolean) ?? true,
        use_auto_color_refs:
          (mainRenderSettings.use_auto_color_refs as boolean) ?? true,
        // Pro+ engine toggles — absent in old snapshots (the API only
        // recently started serializing these keys) → off.
        is_hires: (mainRenderSettings.is_hires as boolean) ?? false,
        is_extended_thinking:
          (mainRenderSettings.is_extended_thinking as boolean) ?? false,
      };
    }

    // Default values (main angle with no prior render) → seed the main angle's
    // default sketch (if any). Null/absent default_control_id keeps it empty.
    const mainDefaultControlId = mainAngle?.attributes.default_control_id;

    return {
      version: 1,
      seed: generateSeed(),
      prompts: { prompt_concept: '', prompt_angle: '' },
      controls: mainDefaultControlId ? [{ id: mainDefaultControlId }] : null,
      style_ids: null,
      material_ids: null,
      colors: null,
      brand_selections: null,
      output_style_id: null,
      environment_setting: EnvironmentSettingEnum.NO_CHANGE,
    };
  }, [
    angleId,
    derivativeRenderSettings,
    mainRenderSettings,
    mainAngle,
    currentAngle,
  ]);

  return {
    mainAngle,
    currentAngleControls,
    angle,
    defaultValues,
    angleTabs,
    mainRenderSettings,
    isRefineInProgress,
    refineParentRenderUrl,
    refineParentRenderId,
    isCreating,
    error,
    hasAnyChanges,
    handleCreateRender,
    resetChangesToDefaults,
    renderSettingsRef,
    tButton,
  };
}
